(ns practitest.frontend.summary.events
  (:require [ajax.core :as ajax]
            [re-frame.core :as re-frame]
            [tick.core :as t]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.components2.dashboard.events :as dashboard-events]
            [practitest.frontend.components2.dashboard.tab-events :as tab-events]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]))

(def db-key :summary)

(defn- current-tab-id [db]
  (get-in db [db-key :current-tab-id]))

;; Keeps ?id= in sync with the selected tab without reloading the page
(re-frame/reg-fx
 ::replace-url-tab-id
 (fn [tab-id]
   (let [url (js/URL. (.-href js/window.location))]
     (.set (.-searchParams url) "id" tab-id)
     (js/history.replaceState nil "" (.toString url)))))

(re-frame/reg-event-fx
 ::fetch
 (fn [{:keys [db]} [_ tab-id]]
   {:db         (assoc-in db [db-key :loading?] true)
    :http-xhrio {:method          :get
                 :uri             (broutes/get-url "summary#index"
                                                   {:p_id (subs/get-current-project-id db)}
                                                   (cond-> {:format "json"}
                                                     tab-id (assoc :id tab-id)))
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [::fetched]
                 :on-failure      [::fetch-failed]}}))

;; item-status is reset, so :reload-key is bumped to remount the cards and have them load again
(defn- store-fetched [db fields]
  (-> db
      (update db-key merge {:loading?    false
                            :load-error? false
                            :item-status {}}
              fields)
      (update-in [db-key :reload-key] (fnil inc 0))))

(re-frame/reg-event-fx
 ::fetched
 (fn [{:keys [db]} [_ {:keys [current_tab_id tabs items tab_filter tab_filter_fields new_tab permissions]}]]
   {:db                 (store-fetched db {:current-tab-id    current_tab_id
                                           :tabs              (vec tabs)
                                           :items             (vec items)
                                           :tab-filter        tab_filter
                                           :tab-filter-fields tab_filter_fields
                                           :new-tab           new_tab
                                           :permissions       permissions})
    ::replace-url-tab-id current_tab_id}))

(re-frame/reg-event-fx
 ::fetch-failed
 (fn [{:keys [db]} [_ response]]
   {:db       (update db db-key assoc :loading? false :load-error? true)
    :dispatch [::notifications/error2 (tab-events/error-message response)]}))

(re-frame/reg-event-fx
 ::request-failed
 (fn [_ [_ response]]
   {:dispatch [::notifications/error2 (tab-events/error-message response)]}))

(re-frame/reg-event-db
 ::reset
 (fn [db _]
   (dissoc db db-key)))

(re-frame/reg-event-fx
 ::select-tab
 (fn [{:keys [db]} [_ tab-id]]
   (when (not= tab-id (current-tab-id db))
     {:dispatch [::fetch tab-id]})))

;; ---- Item charts (loaded lazily by each card) ----

;; Fetches the chart data here (instead of dispatching the chart load event directly) so a
;; failure is tracked per card; the response is then handed to the chart's own success event.
(defn- item-request [{:keys [id item_type data_url]} on-failure]
  (when-let [load-evt (get dashboard-events/item-type->load-event item_type)]
    {:method          :get
     :uri             data_url
     :format          (ajax/json-request-format)
     :response-format (ajax/json-response-format)
     :on-success      [::item-loaded id load-evt]
     :on-failure      on-failure}))

;; Shows a spinner while loading, and an error card with Retry on failure
(re-frame/reg-event-fx
 ::load-item
 (fn [{:keys [db]} [_ {:keys [id] :as item}]]
   (when-let [request (item-request item [::item-load-failed id])]
     {:db         (assoc-in db [db-key :item-status id] :loading)
      :http-xhrio request})))

(re-frame/reg-event-fx
 ::item-loaded
 (fn [{:keys [db]} [_ id load-evt response]]
   {:db       (assoc-in db [db-key :item-status id] :loaded)
    :dispatch [(dashboard-events/loaded-event load-evt) nil response]}))

(re-frame/reg-event-db
 ::item-load-failed
 (fn [db [_ id]]
   (assoc-in db [db-key :item-status id] :error)))

;; A card loads the first time it scrolls into view
(re-frame/reg-event-fx
 ::item-visible
 (fn [{:keys [db]} [_ {:keys [id] :as item}]]
   (when (nil? (get-in db [db-key :item-status id]))
     {:dispatch [::load-item item]})))

;; Cards are keyed by :reload-key, so bumping it remounts them and they load again when visible
(re-frame/reg-event-db
 ::reload-items
 (fn [db _]
   (-> db
       (assoc-in [db-key :item-status] {})
       (update-in [db-key :reload-key] (fnil inc 0)))))

(re-frame/reg-event-fx
 ::set-tab-filter
 (fn [{:keys [db]} [_ value]]
   (let [tab-id (current-tab-id db)]
     {:http-xhrio {:method          :post
                   :uri             (broutes/get-url "dashboards#update_filter_criteria"
                                                     {:p_id (subs/get-current-project-id db) :id tab-id}
                                                     {:format "json"})
                   :params          {:dashboard_id              tab-id
                                     :tab_field_filter_criteria value
                                     :session_type              "dashboards"}
                   :format          (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::tab-filter-set value]
                   :on-failure      [::request-failed]}})))

(re-frame/reg-event-fx
 ::tab-filter-set
 (fn [{:keys [db]} [_ value]]
   {:db       (assoc-in db [db-key :tab-filter :value] value)
    :dispatch [::reload-items]}))

;; ---- External dashboard / embedded item (no login, read only) ----
;; The response has the cards like the Summary page's, plus the dashboard's info under :external

(re-frame/reg-event-fx
 ::fetch-external
 (fn [{:keys [db]} [_ url]]
   {:db         (update db db-key assoc :loading? true :external-url url)
    :http-xhrio {:method          :get
                 :uri             url
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [::external-fetched]
                 :on-failure      [::external-fetch-failed]}}))

(re-frame/reg-event-db
 ::external-fetched
 (fn [db [_ {:keys [items tab_filter] :as response}]]
   (store-fetched db {:items      (vec items)
                      :tab-filter tab_filter
                      :updated-at (t/now)
                      :external   (select-keys response [:title :archived :dashboard_id :project_id :filter_url
                                                         :auto_refresh_minutes])})))

;; No notifications on the external page - the error is shown in place of the cards
(re-frame/reg-event-db
 ::external-fetch-failed
 (fn [db _]
   (update db db-key assoc :loading? false :load-error? true)))

;; The external page keeps its own value (in the session), apart from the Summary page's
(re-frame/reg-event-fx
 ::set-external-tab-filter
 (fn [{:keys [db]} [_ value]]
   (let [{:keys [dashboard_id project_id filter_url]} (get-in db [db-key :external])]
     {:http-xhrio {:method          :post
                   :uri             filter_url
                   :params          {:dashboard_id              dashboard_id
                                     :project_id                project_id
                                     :tab_field_filter_criteria value
                                     :session_type              "external_dashboards"}
                   :format          (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::tab-filter-set value]
                   :on-failure      [::external-fetch-failed]}})))

;; ---- Reorder (drag & drop) ----
;; The new order is applied right away and rolled back if saving fails

(re-frame/reg-event-fx
 ::sort-tabs
 (fn [{:keys [db]} [_ ordered-ids]]
   (let [previous (get-in db [db-key :tabs])
         by-id    (into {} (map (juxt :id identity)) previous)]
     {:db         (assoc-in db [db-key :tabs] (mapv by-id ordered-ids))
      :http-xhrio {:method          :post
                   :uri             (broutes/get-url "dashboards#sort"
                                                     {:p_id (subs/get-current-project-id db)}
                                                     {:format "json"})
                   :params          {:dashboard_ids ordered-ids}
                   :format          (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::sorted]
                   :on-failure      [::sort-failed :tabs previous]}})))

(re-frame/reg-event-fx
 ::sort-items
 (fn [{:keys [db]} [_ ordered-ids]]
   (let [previous (get-in db [db-key :items])
         by-id    (into {} (map (juxt :id identity)) previous)]
     {:db         (assoc-in db [db-key :items] (mapv by-id ordered-ids))
      :http-xhrio {:method          :post
                   :uri             (broutes/get-url "dashboard_items#sort"
                                                     {:p_id (subs/get-current-project-id db)}
                                                     {:format "json"})
                   :params          {:dashboard_id (current-tab-id db) :item_ids ordered-ids}
                   :format          (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::sorted]
                   :on-failure      [::sort-failed :items previous]}})))

(re-frame/reg-event-db
 ::sorted
 (fn [db _] db))

;; ---- Resize (drag a card edge) ----
;; Card sizes are "single" / "double" / "triple"; the backend stores "regular" for single

(defn- set-item-size [db id {:keys [width height]}]
  (cond-> (update-in db [db-key :items] (fn [items]
                                          (mapv #(if (= (:id %) id) (assoc % :width width :height height) %) items)))
    ;; Charts read their size from the loaded chart data (x-axis labels, table height)
    (get-in db [:dashboard-items id])
    (update-in [:dashboard-items id] assoc
               :chart/cell-width (if (= width "single") :regular (keyword width))
               :chart/cell-height (if (= height "double") :double :regular))))

(re-frame/reg-event-fx
 ::resize-item
 (fn [{:keys [db]} [_ id {:keys [width height] :as size}]]
   (let [previous (select-keys (some #(when (= (:id %) id) %) (get-in db [db-key :items])) [:width :height])]
     {:db         (set-item-size db id size)
      :http-xhrio {:method          :patch
                   :uri             (broutes/get-url "dashboard_items#resize"
                                                     {:p_id (subs/get-current-project-id db) :id id}
                                                     {:format "json"})
                   :params          {:cell_width  (if (= width "single") "regular" width)
                                     :cell_height (if (= height "double") "double" "regular")}
                   :format          (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::sorted]
                   :on-failure      [::resize-failed id previous]}})))

(re-frame/reg-event-fx
 ::resize-failed
 (fn [{:keys [db]} [_ id previous response]]
   {:db       (set-item-size db id previous)
    :dispatch [::notifications/error2 (tab-events/error-message response)]}))

(re-frame/reg-event-fx
 ::sort-failed
 (fn [{:keys [db]} [_ k previous response]]
   {:db       (assoc-in db [db-key k] previous)
    :dispatch [::notifications/error2 (tab-events/error-message response)]}))

;; ---- Tab action results (requests are in components2.dashboard.tab-events) ----
;; After a tab action the summary is refetched, so the tab bar, current tab and items stay consistent.

(re-frame/reg-event-fx
 ::tab-action-done
 (fn [{:keys [db]} [_ {:keys [message select-id]} _response]]
   {:dispatch-n [[::fetch (or select-id (current-tab-id db))]
                 (when message [::notifications/success2 message])]}))

;; Like legacy, a cloned tab becomes the selected tab
(re-frame/reg-event-fx
 ::tab-cloned
 (fn [_ [_ {:keys [tab]}]]
   {:dispatch [::tab-action-done {:select-id (:id tab)
                                  :message   (get-translation "dashboards.notify.cloned" "The dashboard tab was cloned.")}]}))

;; Archived / deleted tab: move to the next tab when it was the current one
(re-frame/reg-event-fx
 ::tab-removed
 (fn [{:keys [db]} [_ tab-id message _response]]
   (let [tabs      (get-in db [db-key :tabs])
         idx       (first (keep-indexed #(when (= (:id %2) tab-id) %1) tabs))
         remaining (vec (remove #(= (:id %) tab-id) tabs))
         next-id   (when (= tab-id (current-tab-id db))
                     (:id (get remaining (min (or idx 0) (dec (count remaining))))))]
     {:dispatch [::tab-action-done {:select-id next-id :message message}]})))

;; select = :select switches to the saved tab (new tab)
(re-frame/reg-event-fx
 ::tab-saved
 (fn [{:keys [db]} [_ select message {:keys [tab]}]]
   {:db       (update db db-key dissoc :edit-modal)
    :dispatch [::tab-action-done {:select-id (when select (:id tab)) :message message}]}))

(re-frame/reg-event-db
 ::key-regenerated
 (fn [db [_ {:keys [tab]}]]
   (update-in db [db-key :tabs]
              (fn [tabs] (mapv #(if (= (:id %) (:id tab)) (merge % (dissoc tab :items :can_add_item)) %) tabs)))))
